import { useState } from 'react';

import {
  EmploymentAdditionalProperty,
  EmploymentAdditionalPropertyType
} from 'generated';
import { useSelector } from 'hooks';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from 'react-query';

import { formatByLocale, fromISOString } from 'utils/date';

import { mutations, queries, queryKeys } from 'RestAPIs/rapini';

import { Box } from '@mui/material';
import {
  GridColDef,
  GridRenderCellParams,
  GridRowId
} from '@mui/x-data-grid-pro';

import AddEditAdditionalInformation from 'components/employee-card/employment/additionalProperties/AddEditAdditionalProperty';
import { showSnackbar } from 'components/shared';
import TableCRUDLayout from 'components/shared/CRUD/TableCRUDLayout';

type Properties = {
  employmentId: number;
};

const dateFormatter = (
  params: GridRenderCellParams<EmploymentAdditionalProperty>
) => (params.value && formatByLocale(fromISOString(params.value), 'P')) || '-';

export default function AdditionalPropertiesGrid(props: Properties) {
  const { employmentId } = props;
  const { t } = useTranslation();

  const columns: GridColDef<EmploymentAdditionalProperty>[] = [
    {
      field: 'propertyType',
      headerName: t('employmentAdditionalPropertyType'),
      renderCell: (params) => {
        const propertyType = params.value as EmploymentAdditionalPropertyType;
        return t(`employmentAdditionalPropertyTypes.${propertyType}`);
      },
      flex: 0,
      width: 300
    },
    {
      field: 'propertyValue',
      headerName: t('employmentAdditionalPropertyValue'),
      renderCell: (params) => {
        switch (params.row.propertyType as EmploymentAdditionalPropertyType) {
          case 'COMPANY_DETAILS':
            return (
              <Box display="flex" flexDirection="column">
                {params.value.addressLine1 && (
                  <Box>{params.value.addressLine1}</Box>
                )}
                {params.value.addressLine2 && <>{params.value.addressLine2}</>}
                {params.value.addressLine3 && (
                  <>, {params.value.addressLine3}</>
                )}
                {params.value.addressLine4 && (
                  <>, {params.value.addressLine4}</>
                )}
                {params.value.addressLine5 && (
                  <>, {params.value.addressLine5}</>
                )}
              </Box>
            );
          case 'IDENTITY':
            return `${t(
              `employmentAdditionalPropertyValueIDTypes.${params.value.propertyValueIDType}`
            )}: ${params.value.value}`;
            break;
          default:
            return params.value.value;
            break;
        }
      },
      flex: 1,
      minWidth: 700
    },
    {
      field: 'validFrom',
      headerName: t('validFrom'),
      renderCell: dateFormatter
    },
    {
      field: 'validTo',
      headerName: t('validTo'),
      renderCell: dateFormatter
    }
  ];

  const queryClient = useQueryClient();

  const { data: additionalProperties } =
    queries.useGetEmploymentAdditionalProperties(employmentId);

  const { mutate: deleteAdditionalProperty } =
    mutations.useDeleteEmploymentAdditionalProperty({
      onError: (e: any) => {
        showSnackbar({
          severity: 'error',
          message: t(e?.response?.data?.error?.message || 'failed')
        });
      },
      onSuccess: () => {
        queryClient.invalidateQueries(
          queryKeys.getEmploymentAdditionalProperties(employmentId)
        );
        showSnackbar({
          severity: 'success',
          message: t('employmentAdditionalPropertyDeleteSuccess')
        });
      }
    });

  const [
    shouldShowAddEditAdditionalProperty,
    setShouldShowAddEditAdditionalProperty
  ] = useState<boolean>(false);

  const [additionalProperty, setAdditionalProperty] =
    useState<EmploymentAdditionalProperty | null>(null);

  function handleEditClick(id: number | string) {
    const additionalProperty = additionalProperties?.find(
      (property) => property.employmentAdditionalPropertyId === id
    );
    setAdditionalProperty(additionalProperty || null);
    setShouldShowAddEditAdditionalProperty(true);
  }
  function handleAddClick() {
    setAdditionalProperty(null);
    setShouldShowAddEditAdditionalProperty(true);
  }

  function handleClose() {
    setShouldShowAddEditAdditionalProperty(false);
  }

  function handleDeleteClick(id: GridRowId) {
    deleteAdditionalProperty({
      employmentAdditionalPropertyId: +id,
      employmentId
    });
  }

  const { hasEmploymentAdditionalPropertiesEdit } = useSelector(
    (state) => state.grants
  );

  return (
    <>
      <TableCRUDLayout
        columns={columns}
        renderFormContainer={false}
        shouldRenderToolBar={false}
        editable={false}
        rows={additionalProperties || []}
        onEditClick={(id) => handleEditClick(id)}
        onAddClick={() => handleAddClick()}
        confirmationTitle={t(
          'employmentAdditionalPropertyDeleteConfirmationTitle'
        )}
        confirmationBody={t(
          'employmentAdditionalPropertyDeleteConfirmationBody'
        )}
        onDeleteClick={(id) => handleDeleteClick(id[0])}
        getRowId={(row: EmploymentAdditionalProperty) =>
          row.employmentAdditionalPropertyId || row.employmentId
        }
        disableActions={!hasEmploymentAdditionalPropertiesEdit}
        addRowDialog={
          <AddEditAdditionalInformation
            employmentId={employmentId}
            additionalProperty={additionalProperty}
            isOpen={shouldShowAddEditAdditionalProperty}
            handleClose={handleClose}
          />
        }
      />
    </>
  );
}
